import React, { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { getProvider, getSigner, getContracts } from "../../lib/contracts";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                     */
/* -------------------------------------------------------------------------- */

const fmt = (wei) => parseFloat(ethers.utils.formatEther(wei)).toFixed(4);

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

function TokenSale() {
  const [account, setAccount]     = useState(null);
  const [contracts, setContracts] = useState(null); // read-only
  const [signer, setSigner]       = useState(null);
  const [error, setError]         = useState('');

  // Display state
  const [currentPeriod, setCurrentPeriod]               = useState(0);
  const [tokenBalance, setTokenBalance]                  = useState('0');
  const [weiContribution, setWeiContribution]            = useState('0');
  const [weiRaisedPerPeriod, setWeiRaisedPerPeriod]      = useState('0');
  const [startTimeOfNextPeriod, setStartTimeOfNextPeriod] = useState(null);
  const [weiRaised, setWeiRaised]                        = useState('0');
  const [periods, setPeriods]                            = useState('');
  const [walletsAuthCount, setWalletsAuthCount]          = useState(0);
  const [walletsAuthorized, setWalletsAuthorized]        = useState([]);

  // Input state
  const [ethToSend, setEthToSend]           = useState('0.01');
  const [periodId, setPeriodId]             = useState('0');
  const [claimRangeFrom, setClaimRangeFrom] = useState('0');
  const [claimRangeTo, setClaimRangeTo]     = useState('0');
  const [amountToClaim, setAmountToClaim]   = useState('0.01');
  const [ethToClaim, setEthToClaim]         = useState('0.01');
  const [addressToAdd, setAddressToAdd]     = useState('');
  const [addressPercent, setAddressPercent] = useState('1');
  const [addressTotal, setAddressTotal]     = useState('0.01');

  /* ------------------------------------------------------------------ */
  /*  Bootstrap read-only contracts on mount                             */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    try {
      setContracts(getContracts(getProvider()));
    } catch (e) {
      setError(e.message);
    }

    // Re-connect automatically if MetaMask is already authorised
    if (window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' }).then((accounts) => {
        if (accounts.length > 0) connectWallet();
      });
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          setSigner(null);
          setAccount(null);
        } else {
          connectWallet();
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------------------------------------------ */
  /*  Load chain data whenever contracts or account change               */
  /* ------------------------------------------------------------------ */
  const loadData = useCallback(async (readContracts, userAccount) => {
    if (!readContracts) return;
    try {
      const { crowdsale, token, topLayer } = readContracts;

      const period          = await crowdsale.getCurrentPeriod();
      const raised          = await crowdsale.getWeiRaised();
      const nextPeriodTime  = await crowdsale.getStartTimeOfNextPeriod();
      const authCount       = await topLayer.get_wallets_authorized_count();

      let raisedPeriod = 0;
      let contrib      = 0;
      let balance      = 0;

      if (userAccount) {
        raisedPeriod = await crowdsale.getWeiRaisedPerPeriod(period);
        const data   = await crowdsale.getContributorDataPerPeriod(period, userAccount);
        contrib      = data[2];
        balance      = await token.balanceOf(userAccount);
      }

      setCurrentPeriod(Number(period));
      setWeiRaised(fmt(raised));
      setStartTimeOfNextPeriod(Number(nextPeriodTime));
      setWeiRaisedPerPeriod(fmt(raisedPeriod));
      setWeiContribution(fmt(contrib));
      setTokenBalance(fmt(balance));
      setWalletsAuthCount(Number(authCount));

      // Unclaimed periods for the connected account
      if (userAccount) {
        const unclaimed = [];
        for (let i = 0; i <= Number(period); i++) {
          const contributed = await crowdsale.hasContributedIn(i, userAccount);
          if (contributed) {
            const d = await crowdsale.getContributorDataPerPeriod(i, userAccount);
            if (!d[3]) unclaimed.push(i); // d[3] = claimed flag
          }
        }
        setPeriods(unclaimed.join(', '));
      }

      // Authorized wallets from TopLayer
      const authWallets = [];
      for (let i = 0; i < Number(authCount); i++) {
        const addr        = await topLayer.wallets_authorized_addresses(i);
        const status      = await topLayer.wallets_authorized_to_claim(addr);
        if (status) {
          const pct         = await topLayer.wallets_authorized_percent(addr);
          const total       = await topLayer.wallets_authorized_total(addr);
          const totalClaimed = await topLayer.wallets_authorized_total_claimed(addr);
          authWallets.push([addr, pct, total, totalClaimed, i]);
        }
      }
      setWalletsAuthorized(authWallets);

    } catch (e) {
      console.error('loadData error:', e);
    }
  }, []);

  useEffect(() => {
    loadData(contracts, account);
  }, [contracts, account, loadData]);

  /* ------------------------------------------------------------------ */
  /*  Connect wallet                                                      */
  /* ------------------------------------------------------------------ */
  const connectWallet = async () => {
    setError('');
    try {
      const _signer = await getSigner();
      const _account = await _signer.getAddress();
      const _contracts = getContracts(_signer);
      setSigner(_signer);
      setAccount(_account);
      setContracts(_contracts);
    } catch (e) {
      setError(e.message);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Write transactions (all require signer)                            */
  /* ------------------------------------------------------------------ */
  const requireSigner = () => {
    if (!signer) throw new Error('Please connect your wallet first.');
    return getContracts(signer);
  };

  const buy = async () => {
    try {
      const { crowdsale } = requireSigner();
      if (parseFloat(ethToSend) < 0.01) return;
      const tx = await crowdsale.contributeCurrentPeriod({
        value: ethers.utils.parseEther(ethToSend),
      });
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  const claimPeriod = async () => {
    try {
      const { crowdsale } = requireSigner();
      const tx = await crowdsale.claimPeriod(periodId);
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  const claimAll = async () => {
    try {
      const { crowdsale } = requireSigner();
      const tx = await crowdsale.claimAll();
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  const claimRange = async () => {
    try {
      const { crowdsale } = requireSigner();
      const tx = await crowdsale.claimRange(claimRangeFrom, claimRangeTo);
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  // Claim dev stash tokens (authorized dev wallets)
  const claimDevStash = async () => {
    try {
      const { crowdsale } = requireSigner();
      const tx = await crowdsale.claimDevStashAuthorized(
        ethers.utils.parseEther(amountToClaim)
      );
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  // Claim ETH for authorized addresses (via TopLayer)
  const claimEthAuthorized = async () => {
    try {
      const { topLayer } = requireSigner();
      const tx = await topLayer.claim_eth_authorized_address(
        ethers.utils.parseEther(ethToClaim)
      );
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  const addAuthorizedAddress = async () => {
    try {
      const { topLayer } = requireSigner();
      const tx = await topLayer.add_authorized_address(
        addressToAdd,
        addressPercent,
        ethers.utils.parseEther(addressTotal)
      );
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  const deleteAuthorizedAddress = async (addr, index) => {
    try {
      const { topLayer } = requireSigner();
      const tx = await topLayer.delete_authorized_address(addr, index);
      await tx.wait();
      loadData(contracts, account);
    } catch (e) { setError(e.message); }
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div className="container">
      <br />

      {/* Wallet connection */}
      {!account ? (
        <button className="u-full-width button-primary" onClick={connectWallet}>
          Connect Wallet
        </button>
      ) : (
        <p style={{ wordBreak: 'break-all' }}>
          <strong>Connected:</strong> {account}
        </p>
      )}

      {error && (
        <p style={{ color: 'red' }}>{error}</p>
      )}

      <p>
        ETH contribution: {weiContribution} |
        METH balance: {tokenBalance} |
        Current period ID: {currentPeriod} |
        Current ETH raised: {weiRaisedPerPeriod} |
        Total ETH raised: {weiRaised} |
        Next Period: {startTimeOfNextPeriod
          ? new Date(startTimeOfNextPeriod * 1000).toLocaleString()
          : '—'}
      </p>
      <p>
        Unclaimed periods: {periods || '—'}
      </p>

      <h2>Buy</h2>
      <input
        className="u-full-width"
        type="text"
        placeholder="0.01 ETH"
        value={ethToSend}
        onChange={(e) => setEthToSend(e.target.value)}
      />
      <button className="u-full-width button-primary" onClick={buy}>buy</button>

      <h2>Claim per period</h2>
      <input
        className="u-full-width"
        type="text"
        value={periodId}
        onChange={(e) => setPeriodId(e.target.value)}
      />
      <button className="u-full-width button-primary" onClick={claimPeriod}>claim</button>

      <h2>Claim All</h2>
      <button className="u-full-width button-primary" onClick={claimAll}>claim all</button>

      <h2>Claim Range</h2>
      From:
      <input
        className="u-full-width"
        type="text"
        value={claimRangeFrom}
        onChange={(e) => setClaimRangeFrom(e.target.value)}
      />
      To:
      <input
        className="u-full-width"
        type="text"
        value={claimRangeTo}
        onChange={(e) => setClaimRangeTo(e.target.value)}
      />
      <button className="u-full-width button-primary" onClick={claimRange}>claim range</button>

      <h2>Claim Dev Stash</h2>
      <input
        className="u-full-width"
        type="text"
        value={amountToClaim}
        onChange={(e) => setAmountToClaim(e.target.value)}
      />
      <button className="u-full-width button-primary" onClick={claimDevStash}>claim</button>

      <h2>Claim ETH for Admins</h2>
      <input
        className="u-full-width"
        type="text"
        value={ethToClaim}
        onChange={(e) => setEthToClaim(e.target.value)}
      />
      <button className="u-full-width button-primary" onClick={claimEthAuthorized}>claim</button>

      <h2>Add Authorized Address</h2>
      Address:
      <input
        className="u-full-width"
        type="text"
        value={addressToAdd}
        onChange={(e) => setAddressToAdd(e.target.value)}
      />
      Percent:
      <input
        className="u-full-width"
        type="text"
        value={addressPercent}
        onChange={(e) => setAddressPercent(e.target.value)}
      />
      Total:
      <input
        className="u-full-width"
        type="text"
        value={addressTotal}
        onChange={(e) => setAddressTotal(e.target.value)}
      />
      <button className="u-full-width button-primary" onClick={addAuthorizedAddress}>add</button>

      <h2>Authorized Addresses</h2>
      <table className="u-full-width">
        <thead>
          <tr>
            <th>Address</th>
            <th>Percent</th>
            <th>Total</th>
            <th>Total Claimed</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {walletsAuthorized.map((item) => (
            <tr key={item[0]}>
              <td>{item[0]}</td>
              <td>{item[1].toString()}%</td>
              <td>{fmt(item[2])} ETH</td>
              <td>{fmt(item[3])} ETH</td>
              <td>
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => deleteAuthorizedAddress(item[0], item[4])}
                >
                  delete
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TokenSale;
